import { useState, useRef, useEffect } from 'react';

// Compact dropdown for choosing which series a MultiLineChart draws. A small trigger
// (“Products  n/total ▾”) opens a scrollable checklist — each row shows the series'
// line color (dimmed when off) and a check when on. Hovering a row focuses its line
// via onHover. Designed to live in a Card header so it takes almost no space.
export function ProductPicker({ products, selected, colorFor, onToggle, onSelectAll, onClear, onHover }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-1.5 text-sm font-medium text-slate-600 ring-1 ring-slate-300 transition hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:ring-white/15 dark:hover:bg-white/5"
      >
        <span>Products</span>
        <span className="rounded-md bg-brand/10 px-1.5 py-0.5 text-xs font-semibold text-brand-dark dark:bg-brand/20 dark:text-brand-light">
          {selected.length}/{products.length}
        </span>
        <svg className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 8l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-30 mt-1.5 w-60 overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-slate-900/10 dark:bg-slate-800 dark:ring-white/10">
          <div className="flex items-center justify-between border-b border-slate-900/5 px-3 py-2 dark:border-white/10">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
              {selected.length}/{products.length} shown
            </span>
            <div className="flex items-center gap-2 text-xs">
              <button type="button" onClick={onSelectAll} className="font-medium text-brand hover:text-brand-dark">All</button>
              <span className="text-slate-300 dark:text-slate-600">·</span>
              <button type="button" onClick={onClear} className="font-medium text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300">Clear</button>
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {products.map((name) => {
              const on = selected.includes(name);
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => onToggle(name)}
                  onMouseEnter={onHover ? () => onHover(name) : undefined}
                  onMouseLeave={onHover ? () => onHover(null) : undefined}
                  className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm transition hover:bg-slate-50 dark:hover:bg-white/5"
                >
                  <span className="h-2.5 w-2.5 shrink-0 rounded-sm transition-opacity" style={{ background: colorFor[name], opacity: on ? 1 : 0.3 }} />
                  <span className={`flex-1 truncate ${on ? 'text-navy-800 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500'}`}>{name}</span>
                  {on && (
                    <svg className="h-4 w-4 shrink-0 text-brand" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l3 3 7-7" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
